import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Printer, ShieldCheck, Zap, ArrowRight, Star, Quote, Globe, Mail } from 'lucide-react';

const SEOLandingPage = ({ slug }) => {
  const navigate = useNavigate();

  const pageConfig = {
    'print-near-me-toronto-gta': {
      title: "Print Near Me in Toronto & GTA: Instant Email-to-Print Services",
      subtitle: "The fastest way to print in Scarborough, Pickering, and Downtown Toronto. Skip the library lines and print from any device.",
      headline: "Need a <span>Printer</span> in Toronto Right Now?",
      heroBg: "from-blue-900 to-indigo-800",
      features: [
        { icon: <MapPin />, title: "GTA Wide Network", desc: "Dozens of partner locations across Scarborough, Pickering, and North York." },
        { icon: <Printer />, title: "Laser Quality", desc: "Professional black & white and color laser printing for documents and photos." },
        { icon: <Zap />, title: "Instant Pick-up", desc: "Email your document, get a code, and print in under 60 seconds." }
      ],
      seoContent: `
        <h3>Why QuickPrintHub is Toronto's Favorite Printing Solution</h3>
        <p>Finding a reliable <strong>printing service in Toronto</strong> shouldn't be a marathon. Whether you're a student at UofT needing to print an assignment or a professional in the Financial District needing a last-minute report, QuickPrintHub has you covered.</p>
        <p>Our network of convenience store hubs means you can print while you grab your morning coffee. No USB drives, no drivers, just pure convenience.</p>
      `
    },
    'imprimer-pres-de-moi-sousse': {
      title: "Imprimerie à Sousse : Service d'impression en ligne rapide et local",
      subtitle: "Imprimez vos documents à Sousse en quelques minutes. Envoyez par email, récupérez en magasin. Simple, rapide et efficace.",
      headline: "Besoin d'une <span>Imprimante</span> à Sousse ?",
      heroBg: "from-emerald-900 to-teal-800",
      features: [
        { icon: <MapPin />, title: "Présence Local à Sousse", desc: "Retrouvez-nous dans les zones les plus fréquentées de Sousse." },
        { icon: <ShieldCheck />, title: "Sécurisé et Privé", desc: "Vos documents sont protégés par un code unique à 6 chiffres." },
        { icon: <Printer />, title: "Qualité Pro", desc: "Impression laser de haute qualité pour tous vos besoins administratifs." }
      ],
      seoContent: `
        <h3>La révolution de l'impression à Sousse</h3>
        <p>Cherchez-vous une <strong>imprimerie à Sousse</strong> ouverte tard ? QuickPrintHub transforme les boutiques locales en centres d'impression ultra-rapides. Plus besoin de chercher un cybercafé ouvert.</p>
        <p>Envoyez simplement vos fichiers à print@quickprinthub.com et récupérez vos impressions instantanément chez nos partenaires à Sousse.</p>
      `
    },
    'print-shipping-labels-etsy-ebay-canada': {
       title: "Print Shipping Labels Near Me: The Etsy & eBay Seller's Secret Weapon",
       subtitle: "Stop wasting money on home ink and expensive thermal printers. Use QPH for professional, smudge-proof shipping labels starting today.",
       headline: "Shipping <span>Labels</span> Made Simple & Cheap",
       heroBg: "from-orange-900 to-red-800",
       features: [
         { icon: <Zap />, title: "Smudge-Proof labels", desc: "Sharp laser barcodes ensure your packages are always scannable by Canada Post or USPS." },
         { icon: <Quote />, title: "Save on Ink", desc: "Ditch the 'ink anxiety'. No more buying $50 cartridges for 10 labels." },
         { icon: <Globe />, title: "Eco-Friendly", desc: "Reduce e-waste by using shared community hubs instead of personal hardware." }
       ],
       seoContent: `
         <h3>The Smarter Way to Run Your E-commerce Business</h3>
         <p>As an <strong>Etsy or eBay seller in Canada</strong>, your margins matter. Buying a thermal printer for $200 is a huge upfront cost. QuickPrintHub allows you to print labels professionally on-demand for a fraction of the price.</p>
         <p>Simply forward your label emails to us and pick them up at a Hub near your local post office drop box. It's the ultimate small business hack.</p>
       `
    },
    'digital-nomad-printing-solutions': {
      title: "Digital Nomad Printing Solutions: Print Smarter While Traveling",
      subtitle: "The ultimate lifeline for remote workers and travelers. Email your documents from your hotel or phone and print them at the nearest QPH hub.",
      headline: "The Remote Worker's <span>Office</span> in the Cloud",
      heroBg: "from-slate-900 to-indigo-950",
      features: [
        { icon: <Globe />, title: "Global Freedom", desc: "Access professional printing in new cities without searching for libraries or business centers." },
        { icon: <Zap />, title: "No Tech Setup", desc: "No drivers. No software. If you can send an email, you can print anywhere." },
        { icon: <ShieldCheck />, title: "Complete Privacy", desc: "Secure 6-digit codes ensure your sensitive business documents stay private." }
      ],
      seoContent: `
        <h3>Printing for the Modern Nomad</h3>
        <p>Being a <strong>digital nomad</strong> means freedom, but it shouldn't mean logistical headaches. Whether you're a freelancer needing to sign a contract or a remote employee printing travel vouchers, QuickPrintHub is your physical office away from home.</p>
        <p>Forget the frustration of non-functional hotel printers or expensive local shops. With high-speed laser printing and locations that fit your travel schedule, we bring the office to you.</p>
      `
    },
    'print-near-uoft-scarborough': {
      title: "Print Near UofT Scarborough: Instant Student Document Services",
      subtitle: "The fastest way for UTSC students to print assignments and resumes. Skip the long library lines and print from any device in under 60 seconds.",
      headline: "UTSC <span>Printing</span> Made Easier",
      heroBg: "from-red-900 to-rose-800",
      features: [
        { icon: <MapPin />, title: "Campus-Adjacent", desc: "Multiple Hubs located within walking distance of the UTSC campus and transit hubs." },
        { icon: <Zap />, title: "Late Night Access", desc: "Many partner locations stay open late, perfect for those midnight assignment deadlines." },
        { icon: <Printer />, title: "Reliable Quality", desc: "No more paper jams. Get professional laser prints for every assignment." }
      ],
      seoContent: `
        <h3>Student's Printing Lifeline at UofT Scarborough</h3>
        <p>Finding a working printer at <strong>UofT Scarborough (UTSC)</strong> during finals week shouldn't be your biggest challenge. QuickPrintHub provides students with a reliable, instant alternative to campus computer labs.</p>
        <p>Whether you need to print a 50-page thesis or a one-page resume for a career fair at the Pan Am Centre, our email-to-print process is designed for your busy schedule. Just forward your PDF and pick it up at a nearby convenience store Hub on your way to class.</p>
      `
    },
    'impression-rapide-sousse-centre-ville': {
      title: "Impression Rapide à Sousse Centre-Ville : Documents et Photos",
      subtitle: "Le service d'impression le plus rapide au cœur de Sousse. Envoyez vos fichiers par email et récupérez-les instantanément.",
      headline: "Imprimez au <span>Centre-Ville</span> de Sousse",
      heroBg: "from-cyan-900 to-blue-800",
      features: [
        { icon: <MapPin />, title: "Coeur de Sousse", desc: "Nos Hubs sont idéalement situés près des zones administratives et commerciales." },
        { icon: <ShieldCheck />, title: "100% Sécurisé", desc: "Confidentialité totale pour vos documents administratifs et personnels." },
        { icon: <Printer />, title: "Qualité Laser", desc: "Des impressions nettes et professionnelles pour tous vos dossiers." }
      ],
      seoContent: `
        <h3>Simplifiez vos démarches à Sousse</h3>
        <p>Plus besoin de chercher un cybercafé au <strong>Centre-Ville de Sousse</strong>. QuickPrintHub révolutionne l'impression locale en transformant vos boutiques de quartier en centres d'impression ultra-modernes.</p>
        <p>Idéal pour les professionnels, les étudiants et toute personne ayant besoin d'une impression immédiate sans installation de logiciel. Envoyez simplement un email à print@quickprinthub.com et utilisez votre code unique chez nos partenaires locaux.</p>
      `
    },
    'print-documents-sousse-ar': {
      title: "طباعة وثائق في سوسة: خدمة سريعة وسهلة",
      subtitle: "أسرع طريقة لطباعة أوراقك في سوسة. ابعث ملفاتك بالإيميل واستلمها في الحين من أقرب نقطة ليك.",
      headline: "اطبع أوراقك في <span>سوسة</span> بكل سهولة",
      heroBg: "from-amber-900 to-orange-800",
      features: [
        { icon: <MapPin />, title: "قريب منك في سوسة", desc: "تجد نقاطنا في أهم شوارع سوسة وبالقرب من المصالح الإدارية." },
        { icon: <Zap />, title: "طباعة فورية", desc: "ما تستحقش تستنا. ابعث الملف، خوذ الكود واطبع في أقل من دقيقة." },
        { icon: <ShieldCheck />, title: "خصوصية تامة", desc: "ملفاتك محمية بكود شخصي وتتمسح آليا بعد الطباعة." }
      ],
      seoContent: `
        <h3>ثورة الطباعة الرقمية في سوسة</h3>
        <p>لوجت على <strong>مركز طباعة في سوسة</strong> وما لقيتش؟ QuickPrintHub يسهلك حياتك. توه تنجم تطبع عقودك، أوراقك الإدارية أو حتى تصاورك من غير ما تلوج على مكتبة محلولة.</p>
        <p>الخدمة متاعنا مصممة باش تكون سريعة، اقتصادية وبلا تعقيدات. ابعث الـ PDF متاعك لإيميلنا واستلم أوراقك من أقرب مغازة شريكة لينا في سوسة.</p>
      `
    }
  };

  const config = pageConfig[slug] || pageConfig['print-near-me-toronto-gta'];

  return (
    <div className="min-h-screen bg-white">
      {/* Dynamic Hero Section */}
      <section className={`bg-gradient-to-r ${config.heroBg} text-white py-20 px-4`}>
        <div className="max-w-6xl mx-auto text-center">
          <h1 
            className="text-5xl md:text-7xl font-black mb-6 leading-tight"
            dangerouslySetInnerHTML={{ __html: config.headline }}
          />
          <p className="text-xl md:text-2xl mb-10 text-white/90 max-w-3xl mx-auto leading-relaxed">
            {config.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link 
              to="/" 
              className="bg-yellow-400 text-black px-10 py-5 rounded-full text-xl font-black hover:bg-yellow-300 transition-all shadow-xl flex items-center gap-3"
            >
              Start Printing Now <ArrowRight size={24} />
            </Link>
            <p className="text-white/70 text-sm">No signup required. No app to download.</p>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <div className="bg-gray-50 py-10 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap justify-center gap-8 md:gap-16 items-center opacity-70 grayscale">
          <div className="flex items-center gap-2 font-bold text-xl"><Printer /> Fast Print</div>
          <div className="flex items-center gap-2 font-bold text-xl"><ShieldCheck /> Secure Code</div>
          <div className="flex items-center gap-2 font-bold text-xl"><Zap /> 24/7 Access</div>
        </div>
      </div>

      {/* Features Grid */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12">
            {config.features.map((f, i) => (
              <div key={i} className="group p-8 bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  {f.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{f.title}</h3>
                <p className="text-gray-600 leading-relaxed text-lg">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Long-form Content Section */}
      <section className="py-20 bg-white px-4 border-t border-gray-50">
        <div className="max-w-4xl mx-auto prose prose-xl prose-blue">
          <div 
            className="seo-html-content space-y-6 text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: config.seoContent }}
          />
        </div>
      </section>

      {/* Testimonial / Social Proof */}
      <section className="py-24 bg-blue-50 px-4 rounded-[60px] mx-4 mb-20 overflow-hidden relative">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="flex justify-center mb-6">
            {[1,2,3,4,5].map(s => <Star key={s} className="fill-yellow-400 text-yellow-400" />)}
          </div>
          <blockquote className="text-3xl md:text-4xl font-serif italic text-blue-900 mb-8 leading-tight">
            "The easiest way to print in the GTA. I email my documents from the subway and pick them up at the Hub near my favorite coffee shop."
          </blockquote>
          <cite className="text-xl font-bold text-blue-600 not-italic">— Michael R., Scarborough</cite>
        </div>
        <Quote className="absolute -top-10 -left-10 w-64 h-64 text-blue-100 -z-0 rotate-12" />
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 text-center">
        <h2 className="text-4xl md:text-6xl font-black mb-8 leading-tight">Stop Searching, <br/>Start Printing.</h2>
        <div className="bg-gray-900 text-white inline-block px-12 py-10 rounded-[40px] shadow-2xl">
          <p className="text-2xl mb-6">Send your PDF/Word documents to:</p>
          <div className="flex items-center gap-3 bg-white/10 px-6 py-4 rounded-2xl mb-8 group cursor-pointer hover:bg-white/20 transition-colors">
            <Mail className="text-yellow-400" />
            <code className="text-2xl md:text-3xl font-mono font-bold tracking-tighter">print@quickprinthub.com</code>
          </div>
          <Link 
              to="/" 
              className="bg-blue-600 text-white px-10 py-5 rounded-full text-xl font-bold hover:bg-blue-500 transition-all flex items-center gap-3 justify-center"
            >
              Get My Print Code <ArrowRight size={24} />
          </Link>
        </div>
      </section>

      <footer className="py-12 border-t border-gray-100 text-center text-gray-400">
        <p>© 2026 QuickPrintHub. Global Network Scaling.</p>
      </footer>
    </div>
  );
};

export default SEOLandingPage;
